import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './hooks/useAuth';
import { useActiveUser } from './hooks/useActiveUser';
import { useDarkMode } from './hooks/useDarkMode';
import useNativeSetup from './hooks/useNativeSetup';
import { useSyncEngine } from './services/sync/engine';
import ErrorBoundary from './components/ErrorBoundary';
import AnonymousBanner from './components/AnonymousBanner';
import OfflineBanner from './components/OfflineBanner';
import NudgeBanner from './components/NudgeBanner';
import Login from './pages/Login';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import HabitDetail from './pages/HabitDetail';
import Settings from './pages/Settings';
import Premium from './pages/Premium';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import OAuthCallback from './pages/OAuthCallback';

function BootSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  );
}

function RequireAuth({ children }) {
  const { isAuthed, isLoading } = useActiveUser();
  if (isLoading) return <BootSpinner />;
  if (!isAuthed) return <Navigate to="/login" replace />;
  return children;
}

function AppShell() {
  const { isLoading } = useActiveUser();
  useSyncEngine();

  if (isLoading) return <BootSpinner />;

  return (
    <>
      <OfflineBanner />
      <AnonymousBanner />
      <NudgeBanner />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<OAuthCallback />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/" element={<Dashboard />} />
        <Route path="/habit/:habitId" element={<HabitDetail />} />
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <Settings />
            </RequireAuth>
          }
        />
        <Route
          path="/premium"
          element={
            <RequireAuth>
              <Premium />
            </RequireAuth>
          }
        />
        <Route
          path="/subscription-success"
          element={
            <RequireAuth>
              <SubscriptionSuccess />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  useAuth();
  useDarkMode();
  useNativeSetup();

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
          <AppShell />
          <Toaster
            position="top-center"
            containerStyle={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
            toastOptions={{
              duration: 3500,
              style: {
                borderRadius: '14px',
                padding: '12px 16px',
                fontSize: '14px',
                fontWeight: 500,
                maxWidth: '92vw',
                background: 'var(--toast-bg)',
                color: 'var(--toast-fg)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)',
                border: '1px solid var(--toast-border)',
              },
              success: { iconTheme: { primary: 'var(--app-accent)', secondary: '#fff' }, duration: 2500 },
              error: { iconTheme: { primary: '#ef4444', secondary: '#fff' }, duration: 4500 },
            }}
          />
        </div>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
